import { isAdManagementEnabled, Site } from '../site'

/** `enabled`: the site is individually listed; `enabledByScope`: the whole scope is enabled, whatever the site. */
export type AdminSiteTestBidder = { enabled: boolean; enabledByScope: boolean }
export type AdminSiteTestBidderUpdate = { enabled: boolean }
export type AdminScopeTestBidder = { scope: boolean; sites: string[]; allowedIps: string[] }
export type AdminScopeTestBidderUpdate = { scope: boolean; allowedIps: string[] }

/** The features that put a scope behind the Prebid Server proxy, the only place the test bidder is answered. */
const PROXY_ONBOARDED_FEATURES: readonly string[] = ['pbsStandalone', 'pbstckBidder', 'sparkForPub']
const PROXY_USER_ACCESS_FEATURES: readonly string[] = ['sparkForNetwork']

type ScopeFeatures = { onboardedFeatures?: readonly string[]; userAccessFeatures?: readonly string[] }

/** True iff the scope has at least one proxy feature. */
export const isScopeOnProxy = (scope: ScopeFeatures): boolean =>
  (scope.onboardedFeatures ?? []).some((f) => PROXY_ONBOARDED_FEATURES.includes(f)) || (scope.userAccessFeatures ?? []).some((f) => PROXY_USER_ACCESS_FEATURES.includes(f))

/** Available iff the site is ad-managed AND its scope has at least one proxy feature. */
export const isTestBidderAvailable = (site: Pick<Site, 'adManagementEnabled'>, scope: ScopeFeatures): boolean => isAdManagementEnabled(site) && isScopeOnProxy(scope)

const IPV4_OCTET = /^(0|[1-9]\d{0,2})$/

const isIpv4 = (value: string): boolean => {
  const octets = value.split('.')
  return octets.length === 4 && octets.every((o) => IPV4_OCTET.test(o) && Number(o) <= 255)
}

/** Plain IPv6 only: IPv4-mapped/embedded forms (`::ffff:1.2.3.4`) are rejected, users must enter the plain IPv4 address. */
const isIpv6 = (value: string): boolean => {
  if (!/^[0-9a-fA-F:]+$/.test(value)) return false
  const doubleColonParts = value.split('::')
  if (doubleColonParts.length > 2) return false
  const compressed = doubleColonParts.length === 2
  const all = doubleColonParts.flatMap((part) => (part === '' ? [] : part.split(':')))
  if (!all.every((group) => /^[0-9a-fA-F]{1,4}$/.test(group))) return false
  // Single colons at either edge (":1:2...") are malformed
  if (!compressed && (value.startsWith(':') || value.endsWith(':'))) return false
  return compressed ? all.length < 8 : all.length === 8
}

/** Widest CIDR accepted, so a single entry cannot open the test bidder to a large part of the internet. */
export const LISTERINE_MIN_V4_PREFIX = 24
export const LISTERINE_MIN_V6_PREFIX = 48
export const PREFIX_TOO_WIDE_MESSAGE = `Prefix too wide: minimum /${LISTERINE_MIN_V4_PREFIX} for IPv4, /${LISTERINE_MIN_V6_PREFIX} for IPv6`

const isPrefixTooWide = (value: string): boolean => {
  const slash = value.indexOf('/')
  if (slash === -1) return false
  const prefix = Number(value.slice(slash + 1))
  return prefix < (isIpv4(value.slice(0, slash)) ? LISTERINE_MIN_V4_PREFIX : LISTERINE_MIN_V6_PREFIX)
}

/** Syntax is valid but the CIDR range is wider than allowed; false for anything that is not a valid IP/CIDR. */
export const isIpOrCidrPrefixTooWide = (value: string): boolean => hasValidIpOrCidrSyntax(value) && isPrefixTooWide(value)

/** IPv4 or plain IPv6 address (no IPv4-mapped form, use the plain IPv4), optionally followed by `/prefix` (24-32 for v4, 48-128 for v6). Callers trim. */
const hasValidIpOrCidrSyntax = (value: string): boolean => {
  const slash = value.indexOf('/')
  const address = slash === -1 ? value : value.slice(0, slash)
  const v4 = isIpv4(address)
  if (!v4 && !isIpv6(address)) return false
  if (slash === -1) return true
  const prefix = value.slice(slash + 1)
  return /^(0|[1-9]\d{0,2})$/.test(prefix) && Number(prefix) <= (v4 ? 32 : 128)
}

export const isValidIpOrCidr = (value: string): boolean => hasValidIpOrCidrSyntax(value) && !isPrefixTooWide(value)
